import { useMemo } from 'react';
import {
  useAccountUsersFieldConfig,
  useLocationFieldConfig,
  useOfficeFieldConfig,
  useNoteTakerConfig,
  useDurationFieldConfig,
  useHostFieldConfig,
  useMeetingRoomFieldConfig,
} from '@/formConfigs';
import type { ScheduleMode, ApiReference } from '../../schedule.types';
import { SCHEDULE_INTERVIEW_COPY } from '../../schedule.copy';
import { DateTimeField } from './fields/DateTimeField';
import { TIME_OPTIONS, parse12hrTime } from './fields/timeOptions';
import moment from 'moment-timezone';

const isDateTimeEditingCondition = {
  operator: 'equal' as const,
  field: 'isDateTimeEditing',
  value: 'edit',
  preserveValue: true,
};

const copy = SCHEDULE_INTERVIEW_COPY.details;

const INTERVIEW_TYPE_OPTIONS = [
  { value: 'one_on_one', label: 'One-on-one interview' },
  { value: 'panel', label: 'Panel interview' },
];

interface UseDetailsFormSchemaOptions {
  mode: ScheduleMode;
  apiReference?: ApiReference;
  /** From interview plan — controls meeting room checkbox visibility */
  meetingRoomsEnabled?: boolean;
  /** Current interview type — controls single vs multi interviewer select */
  interviewType?: string;
  /** Currently selected start date — for filtering past time options */
  selectedStartDate?: Date | null;
  /** Currently selected start time — for filtering end time options */
  selectedStartTime?: string;
}

export function useDetailsFormSchema(
  modeOrOptions: ScheduleMode | UseDetailsFormSchemaOptions,
  apiReferenceArg?: ApiReference
) {
  // Support both old signature (mode, apiReference) and new options object
  const options: UseDetailsFormSchemaOptions =
    typeof modeOrOptions === 'string'
      ? { mode: modeOrOptions, apiReference: apiReferenceArg }
      : modeOrOptions;

  const {
    mode,
    // apiReference = 'create',
    meetingRoomsEnabled = false,
    interviewType = 'one_on_one',
    selectedStartDate = null,
    selectedStartTime = '',
  } = options;

  const isManualOrAdhoc = mode === 'manual' || mode === 'adhoc';
  const isSelfSchedule =
    mode === 'self-schedule' || mode === 'self-schedule-adhoc';

  // For manual/adhoc: single select for one_on_one, multi for panel.
  // For self-schedule: always multi.
  const isMultiInterviewer = isSelfSchedule || interviewType === 'panel';

  // ---- FormConfig hooks ----
  const { fieldConfig: interviewerField } = useAccountUsersFieldConfig({
    name: 'participant_ids',
    label: isMultiInterviewer
      ? copy.interviewer.labelPlural
      : copy.interviewer.label,
    isMulti: isMultiInterviewer,
    placeholder: copy.interviewer.placeholder,
  });

  const { fieldConfig: locationField } = useLocationFieldConfig({
    config: {
      label: copy.location.label,
      condition: { operator: 'isNotEmpty', field: 'participant_ids' },
    },
  });

  const { fieldConfig: officeField } = useOfficeFieldConfig({
    config: {
      name: 'ats_office_id',
    },
  });

  const noteTakerField = useNoteTakerConfig();

  const { fieldConfig: durationField } = useDurationFieldConfig({
    name: 'duration',
    label: copy.duration.label,
    unitLabel: copy.duration.unit,
  });

  const { fieldConfig: hostField } = useHostFieldConfig({
    name: 'host_id',
    label: copy.host.label,
    placeholder: 'Select host',
    condition: {
      operator: 'equal',
      field: 'location',
      value: 'zoom',
    },
  });

  const { fieldConfig: meetingRoomField } = useMeetingRoomFieldConfig({
    name: 'meeting_room_ids',
    label: copy.meetingRoom.label,
    isMulti: false,
    placeholder: 'Search meeting rooms',
    condition: {
      operator: 'equal',
      field: 'book_meeting_room',
      value: true,
    },
  });

  // ---- Filtered time options ----
  // startTime: hide past times if selected date is today
  const isSelectedDateToday = selectedStartDate
    ? moment(selectedStartDate).isSame(moment(), 'day')
    : false;
  const nowMins = moment().hours() * 60 + moment().minutes();

  // If the selected startTime is in the past for today, minDate should be tomorrow.
  // Otherwise allow today.
  const startTimeParsedForDate = selectedStartTime
    ? parse12hrTime(selectedStartTime)
    : null;
  const selectedTimeMins = startTimeParsedForDate
    ? startTimeParsedForDate.hours * 60 + startTimeParsedForDate.minutes
    : -1;
  const isStartTimeInPastToday =
    selectedTimeMins >= 0 && selectedTimeMins <= nowMins;

  const datePickerMinDate = useMemo(
    () =>
      isStartTimeInPastToday
        ? moment().add(1, 'day').startOf('day').toDate()
        : new Date(),
    [isStartTimeInPastToday]
  );

  const startTimeOptions = isSelectedDateToday
    ? TIME_OPTIONS.filter((opt) => {
        const p = parse12hrTime(opt.value);
        return p ? p.hours * 60 + p.minutes > nowMins : false;
      })
    : TIME_OPTIONS;

  // endTime: only show times after selected startTime
  const startTimeParsed = selectedStartTime
    ? parse12hrTime(selectedStartTime)
    : null;
  const startTimeMins = startTimeParsed
    ? startTimeParsed.hours * 60 + startTimeParsed.minutes
    : -1;

  const endTimeOptions =
    startTimeMins >= 0
      ? TIME_OPTIONS.filter((opt) => {
          const p = parse12hrTime(opt.value);
          return p ? p.hours * 60 + p.minutes > startTimeMins : false;
        })
      : TIME_OPTIONS;

  // ---- Compose schema ----
  return useMemo(() => {
    // Meeting room fields — only if interview plan has meeting_rooms_enabled
    const meetingRoomFields = meetingRoomsEnabled
      ? [
          {
            type: 'checkbox' as const,
            name: 'book_meeting_room',
            fieldProps: { label: copy.meetingRoom.checkboxLabel },
          },
          meetingRoomField,
        ]
      : [];

    const schema: unknown[] = [
      {
        type: 'sectionLabel',
        label: copy.sectionLabel,
      },
      {
        type: 'input',
        name: 'interviewName',
        label: copy.interviewName.label,
        required: 'Interview name is required',
        fieldProps: {
          placeholder: copy.interviewName.placeholder,
          maxLength: 250,
        },
      },
      {
        type: 'select',
        name: 'interview_type',
        label: copy.interviewType.label,
        options: INTERVIEW_TYPE_OPTIONS,
        fieldProps: { searchable: false, menuPortalTarget: null },
      },
      interviewerField,
      {
        type: 'sectionLabel',
        label: copy.scheduleSectionLabel,
      },
      durationField,
      // Date/Time — manual/adhoc only
      // Info bar (shown when not editing)
      ...(isManualOrAdhoc
        ? [
            // Register isDateTimeEditing as a form field so conditions can watch it
            {
              type: 'input' as const,
              name: 'isDateTimeEditing',
              hidden: true,
            },
            // Info bar — renders null when in edit mode
            {
              type: 'custom' as const,
              name: 'dateTime',
              render: ({
                field,
                formMethods,
              }: {
                field: { value: unknown };
                formMethods: {
                  setValue: (_n: string, _v: unknown) => void;
                  watch: (_n: string) => unknown;
                };
              }) => {
                const mode = formMethods.watch('isDateTimeEditing');
                if (mode === 'edit') return null;
                return (
                  <DateTimeField
                    value={typeof field.value === 'string' ? field.value : ''}
                    onEdit={() =>
                      formMethods.setValue('isDateTimeEditing', 'edit')
                    }
                  />
                );
              },
            },
            // Start date (edit mode)
            {
              type: 'date' as const,
              name: 'startDate',
              label: 'Start date',
              required: 'Start date is required',
              condition: isDateTimeEditingCondition,
              fieldProps: {
                mode: 'single',
                triggerWidth: '100%',
                calendarProps: { minDate: datePickerMinDate },
              },
            },
            // Start time + End time (edit mode, side by side)
            {
              type: 'section' as const,
              columns: 2,
              condition: isDateTimeEditingCondition,
              fields: [
                {
                  type: 'select' as const,
                  name: 'startTime',
                  label: 'Start time',
                  required: 'Start time is required',
                  options: startTimeOptions,
                  colSpan: 1,
                  fieldProps: { searchable: true, menuPortalTarget: null },
                },
                {
                  type: 'select' as const,
                  name: 'endTime',
                  label: 'End time',
                  required: 'End time is required',
                  options: endTimeOptions,
                  colSpan: 1,
                  fieldProps: { searchable: true, menuPortalTarget: null },
                  validate: (
                    value: unknown,
                    allValues: Record<string, unknown>
                  ) => {
                    if (!value || !allValues.startTime) return true;
                    const start = parse12hrTime(allValues.startTime as string);
                    const end = parse12hrTime(value as string);
                    if (!start || !end) return true;
                    const startMin = start.hours * 60 + start.minutes;
                    const endMin = end.hours * 60 + end.minutes;
                    if (endMin <= startMin)
                      return 'End time must be after start time';
                    return true;
                  },
                },
              ],
            },
          ]
        : []),
      {
        type: 'switch' as const,
        name: 'calendar_visibility',
        fieldProps: {
          switchLabel: copy.calendarVisibility.label,
          description: copy.calendarVisibility.subLabel,
          fullWidth: true,
          labelAlign: 'center',
          labelPosition: 'left',
        },
      },
      locationField,
      hostField,
      officeField,
      noteTakerField,
      {
        type: 'switch' as const,
        name: 'fill_with_ai_enabled',
        condition: {
          operator: 'and',
          conditions: [
            { operator: 'isNotEmpty', field: 'participant_ids' },
            { operator: 'equal', field: 'note_taker_enabled', value: true },
            {
              operator: 'in',
              field: 'location',
              value: ['zoom', 'google_meet', 'teams'],
            },
          ],
        },
        fieldProps: {
          switchLabel: 'Allow interviewers to complete scorecards with AI?',
          description:
            'Helps draft scorecard faster, improving completion rates and consistency',
          fullWidth: true,
          labelAlign: 'center',
          labelPosition: 'left',
        },
      },
      ...meetingRoomFields,
    ];

    return schema;
  }, [
    interviewerField,
    durationField,
    meetingRoomField,
    meetingRoomsEnabled,
    locationField,
    officeField,
    hostField,
    noteTakerField,
    isManualOrAdhoc,
    startTimeOptions,
    endTimeOptions,
    datePickerMinDate,
  ]);
}
